import { useRef, useState } from "react";
import { Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useFieldErrors } from "@/components/Tools/use-field-errors";
import { useToast } from "@/hooks/use-toast";
import { type Workspace, useCreateWorkspace, useUpdateWorkspace } from "@/hooks/use-workspaces";
import { ApiError } from "@/lib/api-client";
import { workspaceNameError } from "@/utils/workspace-rules";

interface WorkspaceNameDialogProps {
  /** The workspace being renamed; none to create one. Mounted open, once per use. */
  workspace?: Workspace;
  /** Names the new one may not repeat: the owner's other workspaces the user can see. */
  siblings: string[];
  onClose: () => void;
  /** The plan allows no more workspaces, and Pro would: the caller offers the upgrade. */
  onProRequired: () => void;
}

/** Create a workspace, or rename one: a single name, checked as the server checks it. */
export function WorkspaceNameDialog({ workspace, siblings, onClose, onProRequired }: WorkspaceNameDialogProps) {
  const { toast } = useToast();
  const createWorkspace = useCreateWorkspace();
  const updateWorkspace = useUpdateWorkspace();
  const [name, setName] = useState(workspace?.name ?? "");
  const [saving, setSaving] = useState(false);
  const [refusal, setRefusal] = useState<string>();
  const contentRef = useRef<HTMLDivElement>(null);

  const renaming = workspace !== undefined;
  // An admin renaming someone else's workspace is told whose name it is.
  const ruleErrors = { name: workspaceNameError(name, siblings, workspace?.name, workspace?.role !== "admin") };
  const { field, error, check } = useFieldErrors<"name">({ name: ruleErrors.name ?? refusal });

  const handleSubmit = async () => {
    setRefusal(undefined);
    if (!check(contentRef.current, ruleErrors)) return;
    setSaving(true);
    try {
      if (renaming) {
        await updateWorkspace.mutateAsync({ id: workspace.id, name: name.trim() });
        toast({ title: "Workspace renamed", description: `It is now called ${name.trim()}.` });
      } else {
        await createWorkspace.mutateAsync(name.trim());
        toast({ title: "Workspace created", description: `${name.trim()} is ready.` });
      }
      onClose();
    } catch (failure) {
      // 403 on a create is the plan's cap, which Pro lifts; a 409 is a sentence
      // written for the user (a name in use, or Pro's own cap).
      if (!renaming && failure instanceof ApiError && failure.status === 403) {
        onProRequired();
        return;
      }
      setRefusal(
        failure instanceof ApiError && failure.status === 409
          ? failure.message
          : `The workspace could not be ${renaming ? "renamed" : "created"}. Try again.`,
      );
    } finally {
      setSaving(false);
    }
  };

  // A save in flight finishes in view.
  const handleClose = () => {
    if (!saving) onClose();
  };

  return (
    <Dialog open onOpenChange={(open) => !open && handleClose()}>
      <DialogContent ref={contentRef} className="[&_[aria-invalid=true]]:border-destructive">
        <DialogHeader>
          <DialogTitle>{renaming ? "Rename Workspace" : "Create New Workspace"}</DialogTitle>
          <DialogDescription>
            {renaming ? "Enter a new name for your workspace." : "Give your new workspace a name. You can change this later."}
          </DialogDescription>
        </DialogHeader>
        <form
          className="space-y-2 py-4"
          noValidate
          onSubmit={(event) => {
            event.preventDefault();
            void handleSubmit();
          }}
        >
          <Label htmlFor="workspace-name">Workspace Name</Label>
          <Input
            id="workspace-name"
            placeholder="My Workspace"
            value={name}
            disabled={saving}
            onChange={(event) => {
              setName(event.target.value);
              setRefusal(undefined);
            }}
            {...field("name", "workspace-name")}
          />
          {error("name", "workspace-name")}
        </form>
        <DialogFooter>
          <Button variant="outline" onClick={handleClose} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={() => void handleSubmit()} disabled={saving}>
            {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            {renaming ? (saving ? "Saving…" : "Save") : saving ? "Creating…" : "Create"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
